import { Badge, Box, Button, HStack, Input, SimpleGrid, Text, VStack } from '@chakra-ui/react';
import { TitleBar } from '@resistapp/client/components/bars/title-bar';
import { ResistomapSelect } from '@resistapp/client/components/forms/resistomap-select';
import { theme } from '@resistapp/client/components/shared/theme';
import { Loading } from '@resistapp/client/components/states/loading';
import { resistomapOrganisationId } from '@resistapp/common/features';
import { getProjectTypeInfo } from '@resistapp/common/friendly';
import { ProjectType } from '@resistapp/common/types';
import type { GroupBase } from 'chakra-react-select';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useOrganisations, useProjects } from '../../hooks/api';

export function AdminProjectsView() {
  const { data: projects, isPending, error } = useProjects();
  const { data: organisations } = useOrganisations();

  type Option = { value: string; label: string };
  const [name, setName] = useState('');
  const [types, setTypes] = useState<Option[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<Option>({ value: '100', label: '100' });

  const typeOptions: Option[] = useMemo(
    () => Object.values(ProjectType).map(t => ({ value: t, label: String(t).replace(/_/g, ' ') })),
    [],
  );
  const pageSizeOptions: Option[] = useMemo(() => ['20', '50', '100'].map(n => ({ value: n, label: n })), []);

  const filtered = useMemo(() => {
    const lower = name.trim().toLowerCase();
    const allowedTypes = new Set(types.map(o => o.value));
    return (projects || [])
      .filter(p => {
        const nameOk = !lower || p.name.toLowerCase().includes(lower);
        const typeOk = allowedTypes.size === 0 || allowedTypes.has(p.type);
        return nameOk && typeOk;
      })
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [projects, name, types]);

  const pageN = Number(pageSize.value);
  const start = (page - 1) * pageN;
  const rows = filtered.slice(start, start + pageN);

  const orgsByProjectId = useMemo(() => {
    const map = new Map<number, Array<{ id: number; name: string; isDemo: boolean }>>();
    for (const org of organisations || []) {
      for (const projectId of org.projectAccesses) {
        const arr = map.get(projectId) || [];
        arr.push({ id: org.id, name: org.name, isDemo: org.isDemo });
        map.set(projectId, arr);
      }
    }
    return map;
  }, [organisations]);

  if (error) throw error;

  return (
    <div style={{ marginBottom: theme.spacing[16] }}>
      <TitleBar
        title="Projects"
        $extraWide={true}
        controls={
          <Button as={Link} to="/project/create-empty">
            Create project
          </Button>
        }
      />
      <VStack align="stretch" spacing={4} padding={theme.spacing[4]}>
        <SimpleGrid columns={{ base: 1, md: 3, lg: 4 }} spacing={theme.spacing[3]}>
          <Input
            placeholder="Project name"
            value={name}
            onChange={e => {
              setName(e.target.value);
            }}
            bg="white"
          />
          <ResistomapSelect<Option, true, GroupBase<Option>>
            isMulti
            options={typeOptions}
            value={types}
            onChange={opts => {
              setTypes(opts as Option[]);
            }}
            placeholder="Project type"
            chakraStyles={{ control: base => ({ ...base, backgroundColor: 'white' }) }}
            formatOptionLabel={opt => (
              <Badge colorScheme={typeColorScheme(opt.value as ProjectType)}>
                {String(opt.value).replace(/_/g, ' ')}
              </Badge>
            )}
          />
        </SimpleGrid>

        {isPending ? (
          <Box padding={2} borderRadius={6} bg="white" boxShadow="sm">
            <Loading />
          </Box>
        ) : filtered.length === 0 ? (
          <Text>No projects found.</Text>
        ) : (
          <VStack align="stretch" spacing={2}>
            {rows.map(p => {
              const orgs = orgsByProjectId.get(p.id) || [];
              const externalOrgs = orgs.filter(o => o.id !== resistomapOrganisationId);
              const extCount = externalOrgs.length;
              const singleOrg = extCount === 1 ? externalOrgs[0] : undefined;
              return (
                <Box key={p.id} padding={2} borderRadius={6} bg="white" boxShadow="sm" _hover={{ boxShadow: 'md' }}>
                  <HStack spacing={3} align="baseline">
                    <Text color="gray.600">{p.id}</Text>
                    <Text as={Link} to={`/research/${p.id}`} fontWeight="bold">
                      {p.name}
                    </Text>
                    <Badge colorScheme={typeColorScheme(p.type)} title={getProjectTypeInfo(p.type)}>
                      {p.type}
                    </Badge>
                  </HStack>
                  <HStack spacing={2} mt={1} wrap="wrap">
                    {singleOrg ? (
                      <Badge as={Link} to={`/admin/organisation/${singleOrg.id}`} colorScheme="blue">
                        Org: {singleOrg.name}
                      </Badge>
                    ) : extCount > 1 ? (
                      <Badge colorScheme="blue">Organisations: {extCount}</Badge>
                    ) : null}
                  </HStack>
                </Box>
              );
            })}
            <HStack justify="space-between" mt={2}>
              <Text fontSize="sm">Total: {filtered.length}</Text>
              <HStack>
                <ResistomapSelect<Option, false, GroupBase<Option>>
                  options={pageSizeOptions}
                  value={pageSize}
                  onChange={opt => {
                    if (opt) setPageSize(opt);
                  }}
                  placeholder="Page size"
                  chakraStyles={{ control: base => ({ ...base, backgroundColor: 'white', width: '160px' }) }}
                />
                <Button
                  size="sm"
                  onClick={() => {
                    setPage(p => Math.max(1, p - 1));
                  }}
                  isDisabled={page <= 1}
                >
                  Prev
                </Button>
                <Text>Page {page}</Text>
                <Button
                  size="sm"
                  onClick={() => {
                    setPage(p => p + 1);
                  }}
                  isDisabled={rows.length < pageN}
                >
                  Next
                </Button>
              </HStack>
            </HStack>
          </VStack>
        )}
      </VStack>
    </div>
  );
}

function typeColorScheme(type: ProjectType): string | undefined {
  switch (type) {
    case ProjectType.NORMAL:
      return 'green';
    case ProjectType.POOLED:
      return 'orange';
    case ProjectType.LEGACY:
      return 'gray';
    default:
      return undefined;
  }
}
